import { flexComponentMap } from "components/flexFramework/FlexComponents/flexComponentMap";
import { Logger } from "bernie-logger";
import { ServerSideLogger } from "components/utility/ServerSideLogger";
import { UNSUPPORTED_MODULE } from "config/systemEvents";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { FlexModuleModelStore, FlexModuleModel } from "src/stores/FlexModuleModelStore";
import { LocalizedComponentProps } from "bernie-l10n";
import * as React from "react";
import { computed } from "mobx";
import { FlexComponentMapOptions } from "components/flexFramework/FlexComponents/typings";
import { BlossomComponentTreeStore } from "./BlossomComponentTreeStore";

export interface ComponentData {
  id?: string;
  name: string;
  fmId?: number;
  fmTitleId?: number;
  view?: string;
  scene?: string;
  children?: NewBlossomComponent[];
  flexModuleModel?: FlexModuleModel;
}

export interface ComponentProps extends LocalizedComponentProps {
  context: ExtendedContextStore;
}

export class NewBlossomComponent {
  public id: string;
  public name: string;
  public fmId?: number;
  public fmTitleId?: number;
  public view?: string;
  public scene?: string;
  public children?: NewBlossomComponent[];
  public componentMapping?: FlexComponentMapOptions;

  // don't want these to be serialized, so referencing by function. see constructor
  private getFlexModuleModelStore: () => FlexModuleModelStore;
  private getBlossomComponentTreeStore: () => BlossomComponentTreeStore;

  public get microserviceResponse() {
    return this.getFlexModuleModelStore().modelMap.get(this.id);
  }

  @computed
  public get model() {
    return this.microserviceResponse?.model || {};
  }

  @computed
  public get isMicroserviceResponsePending() {
    const modelMapEntry = this.microserviceResponse;

    return !modelMapEntry ? true : modelMapEntry.responsePending;
  }

  /**
   * Indicates whether component should render, depending on:
   * - currentScene of BlossomComponentTree
   * - isMicroserviceResponsePending()
   * - is microserviceResponse empty or error
   *
   * This logic is not to be considered definitive. Custom logic in a React component can override this implementation
   * Additional factors that might be considered:
   * - does the component have a pending or empty state rendering?
   * - does the component have children? if no children render, should the parent also not render?
   */
  @computed
  public get shouldRender() {
    if (this.scene && this.scene !== this.getBlossomComponentTreeStore().currentScene) {
      return false;
    }

    if (this.isMicroserviceResponsePending || this.microserviceResponse?.empty || this.microserviceResponse?.error) {
      return false;
    }

    return true;
  }

  public constructor(constructorProps: {
    componentData: ComponentData;
    context?: ExtendedContextStore;
    flexModuleModelStore: FlexModuleModelStore;
    blossomComponentTreeStore: BlossomComponentTreeStore;
    logger?: Logger;
  }) {
    const { componentData, context, flexModuleModelStore, blossomComponentTreeStore, logger } = constructorProps;
    const { id, name, fmId, fmTitleId, view, scene, children, flexModuleModel } = componentData;

    this.id = !id ? (fmId ? `${name}${fmId}` : `${name}${Math.round(Math.random() * 100000)}`) : id;
    this.name = name;
    this.fmId = fmId;
    this.fmTitleId = fmTitleId;
    this.view = view;
    this.scene = scene;
    this.children = children;

    this.componentMapping = this.getComponentMapping();

    this.getFlexModuleModelStore = () => flexModuleModelStore;
    this.getBlossomComponentTreeStore = () => blossomComponentTreeStore;

    if (this.componentMapping === undefined && logger && context) {
      this.logUnsupportedModule(logger, context);
    } else if (flexModuleModel) {
      flexModuleModelStore.updateModelMap(this.id, flexModuleModel);
    }
  }

  /**
   * Renders the BlossomComponent using it's reactComponent
   * @param { ComponentProps } componentProps
   * @returns React.Component
   * @memberof NewBlossomComponent
   */
  public render = (componentProps: ComponentProps) => {
    if (!this.componentMapping) {
      return null;
    } else {
      return React.createElement(this.componentMapping.codeSplit as any, {
        l10n: componentProps.l10n,
        context: componentProps.context,
        blossomComponent: this,
        key: this.id,
      });
    }
  };

  /**
   * does the BlossomComponent have any children
   * @return boolean
   * @memberof NewBlossomComponent
   */
  public hasChildren = () => !!(this.children && this.children.length > 0);

  /**
   * returns corresponding mapping object from flexComponetMap using it's name and view
   * Naming convention: <module name>_<view>
   * @returns FlexComponentMapOptions|undefined
   */
  private getComponentMapping = () => {
    if (!flexComponentMap.has(this.name)) {
      return undefined;
    }

    const viewSpecificComponentName = `${this.name}_${this.view}`;
    if (this.view !== undefined && flexComponentMap.has(viewSpecificComponentName)) {
      return flexComponentMap.get(viewSpecificComponentName);
    }

    return flexComponentMap.get(this.name);
  };

  /**
   * Takes the name of a Flex component found in a composition that had no corresponding Flex UI Component, and reports it to the server side logger
   */
  private logUnsupportedModule = (logger: Logger, context: ExtendedContextStore) => {
    const contextualInfo = Object.entries({
      pageType: context.searchContext?.pageType,
      deviceId: context.deviceId,
      userAgent: context.userAgent,
      langId: context.langId,
      locale: context.locale,
      siteId: context.site?.id,
      requestId: context.traceInfo && context.traceInfo["Trace-ID"],
    })
      .map((key, value) => `${key}=\"${value}\"`)
      .toString();

    ServerSideLogger(logger!).logEvent(
      UNSUPPORTED_MODULE,
      `moduleName=${this.name} is not currently supported by this view layer application`,
      contextualInfo
    );
  };
}
